/**
 * Detalle de una búsqueda para el recruiter (spec §3 y §7): la JD aprobada
 * (y sus versiones), la Sourcing Guide con sus 11 secciones (booleans en caja
 * monoespaciada con botón de copiar, spec §8) y el resumen del intake.
 * Acciones: tomar la búsqueda (SAVED → IN_PROCESS con el recruiter asignado),
 * liberarla, cerrarla, y bajar los dos PDFs.
 *
 * NO muestra la transcripción de la entrevista: TA pidió que las conversaciones
 * no queden guardadas (pregunta 14, 29/09/2026) y se purgan al guardar el
 * paquete o al descartar el borrador.
 * Esta vista es la ÚNICA que muestra la guía: solo se monta con acceso TA.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, FileDown, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useAuth } from '@/contexts/SharedAuthContext';
import { useSetTaIntakeStatus, useTaIntakeDetail, useTaIntakePdf } from '@/hooks/useTalentAcquisition';
import { formatDateTime } from '@/lib/utils';
import { guideToText, intakeSummaryToText } from '@/../../shared/ta-intake';
import type { SourcingGuide } from '@/../../shared/types/ta-intake';
import { JdDocument, TaIntakeJdCard } from './TaIntakeJdCard';
import { TaIntakeStatusBadge } from './TaIntakeStatusBadge';

function Seccion({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h4 className="text-[11px] font-bold uppercase tracking-widest text-primary">{n}. {title}</h4>
      {children}
    </section>
  );
}
const Lista = ({ items }: { items: string[] }) => (items.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-sm text-muted-foreground">—</p>);
const Chips = ({ items }: { items: string[] }) => (items.length ? <div className="flex flex-wrap gap-1.5">{items.map((x, i) => <span key={i} className="rounded-full border bg-muted px-2 py-0.5 text-xs">{x}</span>)}</div> : <p className="text-sm text-muted-foreground">—</p>);
const Sub = ({ children }: { children: React.ReactNode }) => <p className="mt-2 text-xs font-semibold text-muted-foreground">{children}</p>;

async function copiar(texto: string, okMsg: string, failMsg: string) {
  try { await navigator.clipboard.writeText(texto); toast.success(okMsg); } catch { toast.error(failMsg); }
}

function GuideView({ guide, jobTitle }: { guide: SourcingGuide; jobTitle: string }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.intake.guide';
  const titulos = (x: { en: string[]; es: string[]; pt: string[] }) => ([['EN', x.en], ['ES', x.es], ['PT', x.pt]] as const).filter(([, v]) => v.length);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
        <span>{t(`${k}.internal_note`)}</span>
        <Button size="sm" variant="ghost" className="h-7 gap-1.5" onClick={() => copiar(guideToText(guide, jobTitle), t(`${k}.copied`), t('talent_acquisition.intake.jd.copy_failed'))}>
          <Copy className="h-3.5 w-3.5" />{t(`${k}.copy_all`)}
        </Button>
      </div>
      <Seccion n={1} title={t(`${k}.s1`)}><Lista items={guide.target_profile} /></Seccion>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30"><Seccion n={2} title={t(`${k}.s2`)}><Lista items={guide.must_have} /></Seccion></div>
        <div className="rounded-lg border p-3"><Seccion n={3} title={t(`${k}.s3`)}><Lista items={guide.nice_to_have} /></Seccion></div>
      </div>
      <Seccion n={4} title={t(`${k}.s4`)}>
        <Sub>{t(`${k}.titles_primary`)}</Sub>
        {titulos(guide.job_titles.primary).map(([l, v]) => <div key={l} className="flex flex-wrap items-center gap-1.5 text-sm"><span className="w-7 text-xs font-semibold text-muted-foreground">{l}</span><Chips items={v} /></div>)}
        <Sub>{t(`${k}.titles_alternative`)}</Sub>
        {titulos(guide.job_titles.alternative).map(([l, v]) => <div key={l} className="flex flex-wrap items-center gap-1.5 text-sm"><span className="w-7 text-xs font-semibold text-muted-foreground">{l}</span><Chips items={v} /></div>)}
        {guide.job_titles.notes && <p className="text-xs text-muted-foreground">{guide.job_titles.notes}</p>}
      </Seccion>
      <Seccion n={5} title={t(`${k}.s5`)}>
        <Sub>{t(`${k}.kw_skills`)}</Sub><Chips items={guide.keywords.skills} />
        <Sub>{t(`${k}.kw_industry`)}</Sub><Chips items={guide.keywords.industry_business_model} />
        <Sub>{t(`${k}.kw_tools`)}</Sub><Chips items={guide.keywords.tools_platforms} />
        <Sub>{t(`${k}.kw_stakeholders`)}</Sub><Chips items={guide.keywords.stakeholders_clients} />
      </Seccion>
      <Seccion n={6} title={t(`${k}.s6`)}>
        <Sub>{t(`${k}.companies_hm`)}</Sub><Chips items={guide.target_companies.hm_mentioned} />
        <Sub>{t(`${k}.companies_direct`)}</Sub>
        <Lista items={guide.target_companies.direct.map((g) => `${g.universe}: ${g.companies.join(', ')} — ${g.why}`)} />
        <Sub>{t(`${k}.companies_extended`)}</Sub>
        <Lista items={guide.target_companies.extended.map((g) => `${g.universe}: ${g.companies.join(', ')} — ${g.why}`)} />
      </Seccion>
      <Seccion n={7} title={t(`${k}.s7`)}>
        <dl className="divide-y rounded-lg border text-sm">
          {guide.linkedin_filters.map((f, i) => <div key={i} className="grid grid-cols-[160px_1fr] gap-2 px-3 py-1.5"><dt className="font-medium">{f.filter}</dt><dd className="text-muted-foreground">{f.recommendation}</dd></div>)}
        </dl>
      </Seccion>
      <Seccion n={8} title={t(`${k}.s8`)}>
        {guide.boolean_searches.map((b, i) => (
          <div key={i} className="rounded-lg border bg-muted/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold">{b.name}</span>
              <Button size="sm" variant="ghost" className="h-7 gap-1.5" onClick={() => copiar(b.query, t(`${k}.copied_boolean`), t('talent_acquisition.intake.jd.copy_failed'))}><Copy className="h-3.5 w-3.5" />{t(`${k}.copy`)}</Button>
            </div>
            <pre className="mt-1 whitespace-pre-wrap wrap-break-word font-mono text-xs">{b.query}</pre>
            {b.note && <p className="mt-1 text-xs text-muted-foreground">{b.note}</p>}
          </div>
        ))}
      </Seccion>
      <Seccion n={9} title={t(`${k}.s9`)}><Lista items={guide.profile_signals} /></Seccion>
      <Seccion n={10} title={t(`${k}.s10`)}><Lista items={guide.red_flags} /></Seccion>
      <Seccion n={11} title={t(`${k}.s11`)}><Lista items={guide.search_strategy} /></Seccion>
    </div>
  );
}

export function TaIntakeDetailDialog({ sessionId, onClose }: { sessionId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.intake.detail';
  const { user } = useAuth();
  const detail = useTaIntakeDetail(sessionId);
  const setStatus = useSetTaIntakeStatus();
  const pdf = useTaIntakePdf();
  const [lang, setLang] = useState<'esp' | 'eng'>('esp');
  if (!sessionId) return null;
  const d = detail.data;
  const s = d?.session;
  const approved = d?.jdVersions.find((v) => v.id === d.approvedJdVersionId);
  const me = user?.email?.toLowerCase() ?? '';
  const mia = !!s?.assignedRecruiter && s.assignedRecruiter === me;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        {/* El encabezado se dibuja SIEMPRE: mientras carga, el diálogo tenía un título
            vacío (Radix avisa por accesibilidad) y se veía como una caja en blanco. */}
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            <span>{s?.jobTitle || t(`${k}.title`)}</span>
            {s && <TaIntakeStatusBadge status={s.status} />}
          </DialogTitle>
          <DialogDescription>
            {s
              ? `${t(`${k}.subtitle`, { hm: s.hmName || s.hmEmail, area: s.areaTeam || '—', created: formatDateTime(s.createdAt) })}${s.languagesLabel ? ` · ${s.languagesLabel}` : ''}`
              : t(`${k}.loading`)}
          </DialogDescription>
        </DialogHeader>
        {detail.isError ? (
          // Sin esto, un error dejaba el spinner girando para siempre.
          <div className="space-y-3 py-10 text-center">
            <p className="text-sm text-muted-foreground">{detail.error?.message || t(`${k}.load_failed`)}</p>
            <Button size="sm" variant="outline" onClick={() => detail.refetch()}>{t('common.retry')}</Button>
          </div>
        ) : detail.isLoading || !d || !s ? (
          <div className="flex items-center justify-center py-16"><WinclapLoader size={32} /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {s.status === 'SAVED' && (
                <Button size="sm" className="gap-1.5" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ sessionId, status: 'IN_PROCESS', assignedRecruiter: me })}>
                  <UserCheck className="h-4 w-4" />{t(`${k}.take`)}
                </Button>
              )}
              {s.status === 'IN_PROCESS' && !mia && (
                <Button size="sm" variant="outline" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ sessionId, status: 'IN_PROCESS', assignedRecruiter: me })}>{t(`${k}.reassign_me`)}</Button>
              )}
              {s.status === 'IN_PROCESS' && (
                <>
                  <Button size="sm" variant="outline" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ sessionId, status: 'SAVED', assignedRecruiter: null })}>{t(`${k}.release`)}</Button>
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ sessionId, status: 'CLOSED' })}><Check className="h-4 w-4" />{t(`${k}.close`)}</Button>
                </>
              )}
              {s.status === 'CLOSED' && (
                <Button size="sm" variant="outline" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ sessionId, status: 'IN_PROCESS', assignedRecruiter: me })}>{t(`${k}.reopen`)}</Button>
              )}
              {s.assignedRecruiter && <span className="text-xs text-muted-foreground">{t(`${k}.assigned_to`, { recruiter: s.assignedRecruiter })}</span>}
              <span className="ml-auto flex items-center gap-1.5">
                {approved && (
                  <Button size="sm" variant="secondary" className="gap-1.5" disabled={pdf.isPending} onClick={() => pdf.mutate({ sessionId, doc: 'jd' })}>
                    {pdf.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}{t(`${k}.pdf_jd`)}
                  </Button>
                )}
                {d.guide && (
                  <Button size="sm" variant="secondary" className="gap-1.5" disabled={pdf.isPending} onClick={() => pdf.mutate({ sessionId, doc: 'guide' })}>
                    {pdf.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}{t(`${k}.pdf_guide`)}
                  </Button>
                )}
              </span>
            </div>
            <Tabs defaultValue={d.guide ? 'guide' : 'jd'} className="mt-2">
              <TabsList>
                <TabsTrigger value="jd">{t(`${k}.tab_jd`)}</TabsTrigger>
                <TabsTrigger value="guide">{t(`${k}.tab_guide`)}</TabsTrigger>
                <TabsTrigger value="intake">{t(`${k}.tab_intake`)}</TabsTrigger>
              </TabsList>
              <TabsContent value="jd" className="mt-4 space-y-4">
                {approved ? (
                  <Tabs value={lang} onValueChange={(v) => setLang(v as 'esp' | 'eng')}>
                    <div className="flex items-center justify-between gap-2">
                      <TabsList><TabsTrigger value="esp">ESP</TabsTrigger><TabsTrigger value="eng">ENG</TabsTrigger></TabsList>
                      <span className="text-xs text-muted-foreground">{t(`${k}.approved_line`, { version: approved.id, at: s.approvedAt ? formatDateTime(s.approvedAt) : '', revisions: s.jdRevisions })}</span>
                    </div>
                    <TabsContent value="esp" className="mt-3"><JdDocument doc={approved.esp} /></TabsContent>
                    <TabsContent value="eng" className="mt-3"><JdDocument doc={approved.eng} /></TabsContent>
                  </Tabs>
                ) : (
                  <p className="text-sm text-muted-foreground">{t(`${k}.no_jd_yet`)}</p>
                )}
                {d.jdVersions.filter((v) => v.id !== approved?.id).length > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    <p className="text-xs font-semibold text-muted-foreground">{t(`${k}.other_versions`)}</p>
                    {d.jdVersions.filter((v) => v.id !== approved?.id).map((v) => <TaIntakeJdCard key={v.id} version={v} canDecide={false} defaultOpen={false} />)}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="guide" className="mt-4">
                {d.guide ? <GuideView guide={d.guide} jobTitle={s.jobTitle} /> : <p className="text-sm text-muted-foreground">{t(`${k}.no_guide_yet`)}</p>}
              </TabsContent>
              <TabsContent value="intake" className="mt-4">
                {d.intakeSummary ? (
                  <pre className="whitespace-pre-wrap rounded-lg border bg-muted/40 p-3 font-sans text-sm">{intakeSummaryToText(d.intakeSummary)}</pre>
                ) : <p className="text-sm text-muted-foreground">{t(`${k}.no_guide_yet`)}</p>}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
